/* eslint-disable no-sequences */
import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import NumberFormat from "react-number-format";
import { Paper } from "@material-ui/core";

import { Button } from "../../../../../../components";
import { Colors } from "../../../../../../constants";
import {
  RoutePaths,
  StockPermissionRoutePaths,
} from "../../../../../../constants/RoutePaths";
import { mediaHelper } from "../../../../../../utils/media-helper";
import { rgba } from "polished";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";

const Subtitle = styled.p`
  width: 100%;
  font-size: 24px;
  margin-top: 32px;
  color: ${Colors.SECONDARY_MEDIUM};
`;

const StockInfoWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const StockInfoContent = styled(Paper)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid ${Colors.GRAY_LIGHT};
  box-shadow: none;
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  padding: ${({ available, warehouse }) =>
    available || warehouse ? "0 16px" : "0 24px"};
  height: ${({ reserved, available }) =>
    reserved ? "60px" : available ? "60px" : "56px"};
  background-color: ${({ reserved, available }) =>
    reserved
      ? rgba(Colors.PRIMARY_BLUE, 0.04)
      : available
      ? Colors.PRIMARY_BLUE
      : "white"};
  border-bottom: ${({ available }) =>
    available ? "none" : `1px solid ${Colors.GRAY_LIGHT}`};
`;

const WarehouseTotal = styled.div`
  font-size: 16px;
  font-weight: 700;
`;

const WarehouseText = styled.div`
  min-width: 170px;
  font-size: 16px;
  color: ${Colors.SECONDARY_DARK};
  font-weight: bold;
`;

const ReservedTotal = styled.div`
  font-size: 14px;
`;

const ReservedText = styled.div`
  min-width: 170px;
  font-size: 14px;
  color: ${Colors.SECONDARY_DARK};
  font-weight: 400;
`;

const ReservedTextWrapper = styled(ReservedText)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const AvailableTotal = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: ${Colors.WHITE};
`;

const AvailableText = styled.div`
  font-size: 16px;
  color: ${Colors.WHITE};
  font-weight: bold;
`;

const ButtonStyled = styled(Button)`
  background-color: transparent;
  box-shadow: none;
  color: ${Colors.SECONDARY_MEDIUM};
  padding: 0px;
  font-size: 13px;
  line-height: 22px;
  &:hover {
    background-color: transparent;
    box-shadow: none;
    color: ${Colors.SECONDARY_MEDIUM};
  }
  &:disabled {
    background-color: transparent;
    box-shadow: none;
  }
  svg {
    font-size: 16px;
    margin-left: 8px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;

  ${mediaHelper.lg`
    width: 100%;
    flex-direction: row;
    align-items: center;
  `}
`;

const StockInfo = ({ ...props }) => {
  const { stockData, isAgentView } = props;
  const history = useHistory();

  return (
    <StockInfoWrapper>
      <Subtitle>Estoque</Subtitle>
      <StockInfoContent>
        <InfoWrapper warehouse>
          <TextWrapper>
            <WarehouseText>No armazém</WarehouseText>
            <WarehouseTotal>
              <NumberFormat
                value={stockData.totalQuantity}
                displayType="text"
                decimalSeparator=","
                thousandSeparator={(true, ".")}
              />
            </WarehouseTotal>
          </TextWrapper>
        </InfoWrapper>
        <InfoWrapper reserved>
          <TextWrapper>
            <ReservedTextWrapper>
              {isAgentView ? "Encomendado" : "Reservado"}
              <ButtonStyled
                size="small"
                variant="contained"
                disabled={stockData.reservedQuantity <= 0}
                onClick={() => {
                  history.push(RoutePaths.ORDER, {
                    materialId: { [stockData.materialId]: true },
                    ERPFileStatus: {},
                    statusList: {
                      1: true,
                      3: true,
                      4: true,
                      5: true,
                      9: true,
                      10: true,
                      11: true,
                      12: true,
                    },
                  });
                }}
              >
                Ver Pedidos <ArrowForwardIcon />
              </ButtonStyled>
            </ReservedTextWrapper>
            <ReservedTotal>
              <NumberFormat
                value={stockData.reservedQuantity}
                displayType="text"
                decimalSeparator=","
                thousandSeparator={(true, ".")}
              />
            </ReservedTotal>
          </TextWrapper>
        </InfoWrapper>
        {!isAgentView && (
          <>
            <InfoWrapper reserved>
              <TextWrapper>
                <ReservedText>Expirado ou vencido</ReservedText>
                <ReservedTotal>
                  <NumberFormat
                    value={stockData.expiredBatchQuantity}
                    displayType="text"
                    decimalSeparator=","
                    thousandSeparator={(true, ".")}
                  />
                </ReservedTotal>
              </TextWrapper>
            </InfoWrapper>
          </>
        )}
        <InfoWrapper reserved>
          <TextWrapper>
            <ReservedText>Data crítica</ReservedText>
            <ReservedTotal>
              <NumberFormat
                value={stockData.warningQuantity}
                displayType="text"
                decimalSeparator=","
                thousandSeparator={(true, ".")}
              />
            </ReservedTotal>
          </TextWrapper>
        </InfoWrapper>
        <InfoWrapper reserved>
          <TextWrapper>
            <ReservedText>Danificado</ReservedText>
            <ReservedTotal>
              <NumberFormat
                value={stockData.damagedQuantity}
                displayType="text"
                decimalSeparator=","
                thousandSeparator={(true, ".")}
              />
            </ReservedTotal>
          </TextWrapper>
        </InfoWrapper>
        <InfoWrapper reserved>
          <TextWrapper>
            <ReservedText>Em falta</ReservedText>
            <ReservedTotal>
              <NumberFormat
                value={stockData.missingProductQuantity}
                displayType="text"
                decimalSeparator=","
                thousandSeparator={(true, ".")}
              />
            </ReservedTotal>
          </TextWrapper>
        </InfoWrapper>
        <InfoWrapper available>
          <TextWrapper>
            <AvailableText>Total disponível</AvailableText>
            <AvailableTotal>
              <NumberFormat
                value={stockData.availableQuantity}
                displayType="text"
                decimalSeparator=","
                thousandSeparator={(true, ".")}
              />
            </AvailableTotal>
          </TextWrapper>
        </InfoWrapper>
      </StockInfoContent>
    </StockInfoWrapper>
  );
};

StockInfo.propTypes = {
  stockData: PropTypes.object,
  isAgentView: PropTypes.bool,
};

StockInfo.defaultProps = {
  stockData: {},
  isAgentView: false,
};

export default StockInfo;
